import React, { useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';

interface BulkActionsProps {
  selectedIds: Set<number>;
  onClearSelection: () => void;
  onDelete: (ids: number[]) => Promise<void>;
  entityName?: string;
}

export const BulkActions: React.FC<BulkActionsProps> = ({ selectedIds, onClearSelection, onDelete, entityName = 'items' }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const count = selectedIds.size;
  if (count === 0) return null;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(Array.from(selectedIds));
      onClearSelection();
      setShowConfirm(false);
    } catch (e) {
      console.error('Bulk delete failed:', e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-gray-900 text-white rounded-xl shadow-2xl px-6 py-3 flex items-center gap-4">
        <span className="text-sm font-medium">{count} selected</span>
        <button onClick={() => setShowConfirm(true)} className="px-4 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium transition-colors">Delete</button>
        <button onClick={onClearSelection} className="px-4 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors">Cancel</button>
      </div>
      <Modal open={showConfirm} onClose={() => setShowConfirm(false)} title="Confirm Delete">
        <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete {count} {entityName}? This action cannot be undone.</p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancel</Button>
          <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">{deleting ? 'Deleting...' : `Delete ${count} ${entityName}`}</Button>
        </div>
      </Modal>
    </>
  );
};

export const useSelection = () => {
  const [selected, setSelected] = React.useState<Set<number>>(new Set());
  const toggle = (id: number) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = (ids: number[]) => setSelected(prev => prev.size === ids.length ? new Set() : new Set(ids));
  const clear = () => setSelected(new Set());
  return { selected, toggle, toggleAll, clear };
};
